import { useDriverManagement } from '@/api/useDriverManagement'
import { useTractorMetadata, useTrailerMetadata } from '@/api/useMetadata/useMasterMetadata'
import { invalidateTypedQueries } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { isDefined } from '@/utils/general'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Drawer, List, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { useEffect } from 'react'
import { TemplatePreview } from '../../Manage/TemplateManagement/TemplatePreview'
import { offsetDayToDate } from '../../Manage/TemplateManagement/utils'
import { useScheduleItems } from '../Context/ScheduleItems/ScheduleItemsContext'
import type { MappedScheduleItem } from '../Context/ScheduleItems/scheduleItemStore'
import { useInitializeItems } from '../Context/ScheduleItems/useInitializeItems'
import { conflictValidators } from '../Context/ScheduleItems/validators'
import type { ConflictError } from '../utils/ConflictValidators'
import { getHourFromShiftStartTime } from './DriverInfoCellRenderer'
import { EquipmentOverrideCard } from './EquipmentOverrideCard'

export const EquipmentScheduleModal = () => {
  const { editingTemplateDriverId, editTemplateForDriver } = useScheduleItems()
  const onClose = () => editTemplateForDriver?.(null)
  return (
    <Drawer
      height={'95vh'}
      placement='bottom'
      title='Edit Template'
      open={!!editingTemplateDriverId}
      footer={null}
      destroyOnClose
      onClose={onClose}
    >
      {!!editingTemplateDriverId && <ModalContent onClose={onClose} />}
    </Drawer>
  )
}

function ModalContent({ onClose }: { onClose: () => void }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null | undefined>()
  const { useDriverTemplates, useDriverOverview, useDriverTemplateUpdate } = useDriverManagement()
  const queryClient = useQueryClient()
  const { data: drivers, isLoading: driversLoading } = useDriverOverview({ onlyActiveDrivers: true })
  const { data: templates, isError: _templatesError, isLoading: templatesLoading } = useDriverTemplates()

  const { editingTemplateDriverId } = useScheduleItems()
  const otherDrivers = useMemo(
    () => drivers?.filter((x) => x.id !== editingTemplateDriverId) || [],
    [drivers, editingTemplateDriverId]
  )

  const { getOverlappingSchedules, isLoading: schedulesLoading } = useInitializeItems(otherDrivers, {
    start: dayjs().startOf('day'),
    end: dayjs().add(30, 'day').endOf('day'),
  })
  // const { data: metadata, isLoading: metadataLoading } = useDriverMetadata()
  const { data: tractors, isLoading: _tractorsLoading } = useTractorMetadata()
  const { data: trailers, isLoading: _trailersLoading } = useTrailerMetadata()

  const [templateStartDate, setTemplateStartDate] = useState<dayjs.Dayjs | null>(dayjs())
  const filteredDriver = drivers?.find((x) => x.id === editingTemplateDriverId)
  const selectedTemplate = templates?.find((x) => x.id === selectedTemplateId)

  const { mutateAsync: updateDriverTemplate, isPending: isTemplateUpdatePending } = useDriverTemplateUpdate()

  useEffect(() => {
    if (!filteredDriver) return
    setSelectedTemplateId(filteredDriver.preferred_template_id ?? null)
  }, [filteredDriver])

  const [overrides, setOverrides] = useState<ReturnType<typeof initializeOverrideState>>([])

  useEffect(() => {
    setOverrides(initializeOverrideState(filteredDriver?.preferred_template_days || []))
  }, [filteredDriver])

  function initializeOverrideState(templateDays: components['schemas']['ScheduleDay'][]) {
    return templateDays.map((day) => ({
      ...day,
      tractor_id: day.tractor_id ?? filteredDriver?.tractor_id,
      trailer_id: day.trailer_id ?? filteredDriver?.trailer_id,
    }))
  }

  const getTemplateStartDate = useCallback(() => {
    return isDefined(selectedTemplate?.offset_day) ? offsetDayToDate(selectedTemplate!.offset_day) : templateStartDate
  }, [selectedTemplate, templateStartDate])

  const templateDates = useMemo(
    () => selectedTemplate?.days?.map((_, index) => dayjs(getTemplateStartDate()).add(index, 'day').toDate()),
    [selectedTemplate, getTemplateStartDate]
  )

  function handleEquipmentChange(type: 'tractor' | 'trailer', value: string, index: number) {
    setOverrides((prev) =>
      prev.map((day, i) => {
        if (i === index) {
          return type === 'tractor' ? { ...day, tractor_id: value } : { ...day, trailer_id: value }
        }
        return day
      })
    )
  }

  const conflicts = useMemo(() => {
    const range = 30
    const weeks = Math.floor(range / overrides.length)
    return overrides.map((day, index) => {
      return Array(weeks)
        .fill(null)
        .map((_, week) => {
          const startDate = getTemplateStartDate()
          if (!startDate || !filteredDriver)
            return { start: startDate, errors: [] as ConflictError<MappedScheduleItem>[] }
          const currentDay = startDate.add(index, 'days').add(week, 'weeks')
          const startDateAdjusted = currentDay.hour(getHourFromShiftStartTime(filteredDriver))
          const end = startDateAdjusted.add(filteredDriver?.shift_length?.shift_length_hours ?? 0, 'hours')
          const item = {
            start: startDateAdjusted,
            end,
            tractor_id: day.tractor_id,
            trailer_id: day.trailer_id,
            event_id: '',
            editable: true,
            driver_or_carrier_id: '',
          }
          const overlaps = getOverlappingSchedules(item)
          return { start: startDateAdjusted, errors: conflictValidators.getInvalidItems(item, overlaps).errors }
        })
        .filter((x) => x.errors.length > 0)
    })
  }, [overrides, getTemplateStartDate, filteredDriver, getOverlappingSchedules])

  return (
    <Horizontal style={{ height: '100%', display: 'flex', gap: 16 }}>
      <Vertical className='bg-1' flex={1} style={{ borderRadius: 4 }}>
        <List
          dataSource={templates || []}
          loading={templatesLoading}
          style={{ overflowY: 'auto' }}
          renderItem={(template) => (
            <List.Item
              style={{
                cursor: 'pointer',
                backgroundColor: selectedTemplateId === template.id ? 'var(--theme-color-2-dim)' : 'transparent',
                borderRadius: 4,
                padding: '8px 16px',
                marginBottom: '4px',
              }}
              onClick={() => {
                setSelectedTemplateId(template.id)
                setOverrides(initializeOverrideState(template.days))
              }}
            >
              <Vertical gap={4} style={{ width: '100%' }}>
                <Texto category='p2'>{template.name}</Texto>
                <Horizontal alignItems='center' gap={8} justifyContent='space-between'>
                  <Texto category='label'>{template.days?.length} day</Texto>
                  <TemplatePreview value={template.days?.map((x) => x.shift)} />
                </Horizontal>
              </Vertical>
            </List.Item>
          )}
        />
      </Vertical>
      <Vertical className='bg-1 p-3' flex={1} style={{ borderRadius: 4 }}>
        {selectedTemplate?.offset_day === null && (
          <>
            <Texto category='heading-small'>Start Date</Texto>
            <DatePicker value={templateStartDate} onChange={setTemplateStartDate} className='mb-3' />
          </>
        )}
        <List
          style={{ flex: 1, overflowY: 'auto' }}
          dataSource={selectedTemplate?.days || []}
          loading={driversLoading}
          renderItem={(item, index) => {
            const firstError = conflicts[index]?.[0]
            const isSelfError = firstError?.errors.some((x) =>
              x.items.some((y) => y.driver_id === editingTemplateDriverId)
            )

            return (
              <List.Item style={{ padding: 0, marginBottom: 8 }}>
                <EquipmentOverrideCard
                  item={item}
                  date={templateDates?.[index]}
                  onTractorChange={(value) => handleEquipmentChange('tractor', value as string, index)}
                  onTrailerChange={(value) => handleEquipmentChange('trailer', value as string, index)}
                  tractorId={overrides[index]?.tractor_id}
                  trailerId={overrides[index]?.trailer_id}
                  tractorMetadata={tractors?.tractors || []}
                  trailerMetadata={trailers?.trailers || []}
                  conflict={isDefined(firstError) && !isSelfError ? firstError : undefined}
                />
              </List.Item>
            )
          }}
        />
        <Horizontal justifyContent='flex-end' className='mb-4 bordered p-3 bg-1'>
          <GraviButton
            loading={isTemplateUpdatePending || schedulesLoading}
            disabled={isTemplateUpdatePending || schedulesLoading}
            buttonText='Apply Edits'
            onClick={async () => {
              await updateDriverTemplate(
                {
                  body: {
                    driver_id: editingTemplateDriverId!,
                    preferred_template_id: selectedTemplateId || null,
                    pattern_schedule_start_date: getTemplateStartDate()
                      ? dayjs.tz(getTemplateStartDate(), 'UTC').startOf('day').toISOString()
                      : null,
                    days: overrides,
                  },
                },
                {
                  onSuccess: () => {
                    message.success('Template updated successfully')
                    onClose()
                  },
                  onError: (err) =>
                    message.error(
                      err?.detail && typeof err.detail === 'string' ? err.detail : 'An unknown error has occurred'
                    ),
                  onSettled: () => {
                    invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview')
                    invalidateTypedQueries(queryClient, 'post', '/driver/info/overview')
                  },
                }
              )
            }}
          />
        </Horizontal>
      </Vertical>
    </Horizontal>
  )
}
